import React from 'react';
import { BANDS, BAND_CLASSES, bandFor } from '../constants/riskAssessment';

/**
 * Horizontal segmented bar visualising HRN-band reduction. Four equal-width
 * colour bands (green → yellow → orange → red). Two markers sit ABOVE the bar:
 *   ▼ Initial HRN (grey)
 *   ▼ Residual HRN (band-coloured, slides on prop change)
 *
 * Why equal widths? Logarithmic *intra*-band positioning already compresses
 * the 500+ range visually; equal-width band segments make the "step down"
 * across bands feel meaningful to a non-technical reader.
 *
 * Props:
 *  - initialHrn: number — caller computes via computeHrn(lo, fe, dph, np)
 *  - residualHrn: number — same
 *  - disabled: bool — applies a dim/grayscale tone
 */

// Intra-band log scale ranges. The "0–5" band has a floor of 0.01 to avoid
// log(0); the "500+" band caps at 25000 (LO·FE·DPH·NP max = 15·5·15·12).
const BAND_RANGES = [
  { min: 0.01, max: 5 },     // Negligible
  { min: 5,    max: 50 },    // Low/Significant
  { min: 50,   max: 500 },   // High
  { min: 500,  max: 25000 }, // Unacceptable
];

function bandIndexFor(hrn) {
  const n = Number(hrn) || 0;
  for (let i = 0; i < BANDS.length; i++) {
    if (n < BANDS[i].max) return i;
  }
  return BANDS.length - 1;
}

/** Map an HRN value to a 0–100 percent position on the equal-width bar. */
function positionFor(hrn) {
  const n = Number(hrn) || 0;
  if (n <= 0) return 0;
  if (n >= 25000) return 100;
  const idx = bandIndexFor(n);
  const { min, max } = BAND_RANGES[idx];
  // log-scaled position WITHIN the band, clamped to [0, 1]
  const safe = Math.max(min, Math.min(n, max));
  const intra = Math.log(safe / min) / Math.log(max / min);
  const clamped = Math.max(0, Math.min(1, intra));
  return ((idx + clamped) / 4) * 100;
}

/** Round small HRNs to 2dp, bigger ones to integers. Mirrors formatHrnPill. */
function formatHrnValue(hrn) {
  const n = Number(hrn) || 0;
  if (n < 10) return Math.round(n * 100) / 100;
  return Math.round(n);
}

// Subdued segment swatches — light-mode uses 100-tone for a soft tint
// (visual context, not a traffic-light alarm), dark-mode uses 900/40 for the
// same restrained feel. Previous 400/500 tones overpowered the form.
const SEGMENT_BG = {
  green:  'bg-green-100  dark:bg-green-900/40',
  yellow: 'bg-yellow-100 dark:bg-yellow-900/40',
  orange: 'bg-orange-100 dark:bg-orange-900/40',
  red:    'bg-red-100    dark:bg-red-900/40',
};

// Marker text tone — used for both label + triangle so the residual marker
// reads as "the colour of the band it has landed in".
const MARKER_TONE = {
  green:  'text-green-600  dark:text-green-400',
  yellow: 'text-yellow-700 dark:text-yellow-400',
  orange: 'text-orange-600 dark:text-orange-400',
  red:    'text-red-600    dark:text-red-400',
};

export default function RiskReductionBar({ initialHrn = 0, residualHrn = 0, disabled = false }) {
  const initBand = bandFor(initialHrn);
  const resBand = bandFor(residualHrn);
  const initPct = positionFor(initialHrn);
  const resPct = positionFor(residualHrn);
  const initVal = formatHrnValue(initialHrn);
  const resVal = formatHrnValue(residualHrn);

  const ariaLabel = `Risk reduced from ${initVal} (${initBand.label}) to ${resVal} (${resBand.label})`;

  return (
    <div className={`w-full ${disabled ? 'opacity-60' : ''}`} data-testid="risk-reduction-bar">
      {/* Top row — Initial marker. Stacked above the residual row so labels
          never overlap when the two values land at similar positions. */}
      <div className="relative h-5">
        <div
          className="absolute -translate-x-1/2 flex flex-col items-center pointer-events-none leading-tight"
          style={{ left: `${initPct}%` }}
        >
          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
            Initial {initVal}
          </span>
          <span className="text-gray-400 dark:text-gray-500 text-[11px] -mt-0.5" aria-hidden="true">▼</span>
        </div>
      </div>

      {/* Second row — Residual marker (band-coloured, slides on prop change) */}
      <div className="relative h-5">
        <div
          className="absolute -translate-x-1/2 flex flex-col items-center pointer-events-none leading-tight"
          style={{ left: `${resPct}%`, transition: 'left 250ms ease-out' }}
        >
          <span className={`text-xs font-medium whitespace-nowrap ${MARKER_TONE[resBand.tone]}`}>
            Residual {resVal}
          </span>
          <span className={`${MARKER_TONE[resBand.tone]} text-[11px] -mt-0.5`} aria-hidden="true">▼</span>
        </div>
      </div>

      {/* The bar itself — slim 4-segment strip. Border very faint. */}
      <div
        role="img"
        aria-label={ariaLabel}
        className="relative w-full h-1.5 rounded-sm overflow-hidden flex border border-gray-200/60 dark:border-gray-700/40"
      >
        {BANDS.map((band, i) => (
          <div
            key={band.key}
            className={`flex-1 ${SEGMENT_BG[band.tone]}`}
            title={`${band.label} (${BAND_RANGES[i].min}–${BAND_RANGES[i].max === 25000 ? '500+' : BAND_RANGES[i].max})`}
          />
        ))}
      </div>

      {/* Band labels under the bar */}
      <div className="grid grid-cols-4 gap-0 mt-1 text-[10px] text-gray-500 dark:text-gray-400">
        {BANDS.map((band) => (
          <span key={band.key} className="text-center truncate">{band.label}</span>
        ))}
      </div>

      {/* Screenreader-only summary using the existing BAND_CLASSES pill */}
      <span className={`sr-only ${BAND_CLASSES[resBand.tone]}`}>
        Residual band: {resBand.label}
      </span>
    </div>
  );
}
